import { Component, OnInit, HostListener, ViewChild, ElementRef } from '@angular/core';
import { faUserTie, faCaretDown, faSignOutAlt, faBars } from '@fortawesome/free-solid-svg-icons';
import { Router, NavigationEnd } from '@angular/router';
import { LoginService } from '../services/login.service';
import { interval } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from 'src/services/authentication.service';
import { Etablissement } from 'src/model/model.etablissement';
import { Role } from 'src/model/model.role';
import { LanguageService } from '../services/language.service';
import { MatSidenav } from '@angular/material/sidenav';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit {
  faCaretDown = faCaretDown;
  faUserTie = faUserTie;
  faBars = faBars;
  signOutAlt = faSignOutAlt;
  isLargeScreen = window.innerWidth > 768;
  isLogin: boolean = false;
  isEtabSelected: boolean = false;
  selectedEtab: Etablissement | null = null;
  userName: string = '';
  inscritsEnv: string = '';
  personnels: string = '';
  personnel: string = '';
  roleList: Role[] = [];
  isLoginPage: boolean = false;
  isSidebarOpen: boolean = window.innerWidth > 768;

  @ViewChild('sidenav') sidenav!: MatSidenav;
  @ViewChild('navbar', { static: false }) navbarRef!: ElementRef;

  constructor(
    public loginService: LoginService,
    public router: Router,
    private translate: TranslateService,
    private authenticationService: AuthenticationService,
    public languageService: LanguageService
  ) {}

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.isLargeScreen = window.innerWidth > 768;
    this.updateNavbarHeight();
    if (this.isLargeScreen) {
      this.isSidebarOpen = true;
      if (this.sidenav) this.sidenav.open();
    } else {
      this.isSidebarOpen = false;
      if (this.sidenav) this.sidenav.close();
    }
  }

  ngOnInit() {
    this.translate.addLangs(['en', 'fr', 'ar']);
    this.translate.setDefaultLang('ar');
    const savedLang = localStorage.getItem('lang');
    if (!savedLang) {
      this.languageService.setLanguage('ar');
    } else {
      this.languageService.setLanguage(savedLang);
    }

    this.languageService.currentLang$.subscribe(lang => {
      this.translate.use(lang);
      document.documentElement.setAttribute('dir', this.languageService.getDirection());
    });

    // Liste des routes publiques (avec et sans préfixes pour compatibilité)
    const publicRoutes = [
      '/', '/login', '/register', '/reset-password', '/email-verification', '/reset-password-user', '/register-user',
      '/auth/login', '/auth/register', '/auth/reset-password', '/auth/email-verification', '/auth/reset-password-user', '/auth/register-user',
      '/success', '/fail', '/user/success', '/user/fail'
    ];

    // Fonction pour vérifier si l'URL est une route publique (ignorant les paramètres)
    // Vérifie aussi si l'URL commence par /auth/ pour gérer les redirections
    const isPublicRoute = (url: string) => {
      const cleanUrl = url.split('?')[0]; // Ignorer les paramètres
      return publicRoutes.includes(cleanUrl) || cleanUrl.startsWith('/auth/');
    };

    this.isLogin = this.authenticationService.isUserLoggedIn();
    this.isLoginPage = isPublicRoute(this.router.url);


    if (!this.isLogin && !this.isLoginPage) {
      this.router.navigate(['/login']);
    }

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const currentUrl = event.urlAfterRedirects.split('?')[0]; // Ignorer les paramètres
        this.isLoginPage = isPublicRoute(currentUrl);
        this.isLogin = this.authenticationService.isUserLoggedIn();
        if (!this.isLogin && !this.isLoginPage) {
          this.router.navigate(['/auth/login'], { queryParams: { expired: 'true' } });
        }
      }
    });

    interval(1000).subscribe(() => {
      const etabData = sessionStorage.getItem('etablissement');
      this.selectedEtab = etabData ? JSON.parse(etabData) : null;
      if (this.selectedEtab) {
        this.inscritsEnv = this.selectedEtab.inscrits || '';
        this.personnels = this.selectedEtab.personnels || '';
        this.personnel = this.selectedEtab.personnel || '';
        this.userName = sessionStorage.getItem('username') || '';
        const authUser = sessionStorage.getItem('authUser');
        this.roleList = authUser ? JSON.parse(authUser)?.roles || [] : [];
      }
      this.isLogin = this.authenticationService.isUserLoggedIn();
      this.isEtabSelected = this.authenticationService.isEtabSelected();
      this.updateNavbarHeight();
    });
  }

  ngAfterViewInit() {
    this.updateNavbarHeight();
  }

  updateNavbarHeight() {
    if (this.navbarRef) {
      const height = this.navbarRef.nativeElement.offsetHeight;
      document.documentElement.style.setProperty('--navbar-height', `${height}px`);
    } else {
      document.documentElement.style.setProperty('--navbar-height', window.innerWidth > 480 ? '64px' : '56px');
    }
  }

  logOut() {
    this.authenticationService.logOut();
    this.isLogin = false;
    this.router.navigate(['/login']);
  }

  toggleSidebar(event: boolean) {
    this.isSidebarOpen = event;
    if (this.sidenav) {
      event ? this.sidenav.open() : this.sidenav.close();
    }
  }

  closeSidebarOnMobile() {
    if (!this.isLargeScreen && this.sidenav) {
      this.isSidebarOpen = false;
      this.sidenav.close();
    }
  }
}
